import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { Entry } from "contentful";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";

import { useObjectConfigurationValue } from "~/contexts/configuration";
import useScript from "~/hooks/use-script";
import Logger from "~/services/logger/logger";
import CrossCountry from "~/types/cross-country";
import Editorials from "~/types/editorials";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import imageUtils from "~/utils/image-utils";

import Grid from "../common/grid";
import NextImage from "../common/next-image";
import SkeletonTris from "../common/skeletons/skeleton-tris";
import RevieveModalTrigger from "../editorials/revieve-modal-trigger/revieve-modal-trigger";
import styles from "./product-beauty-services.module.scss";

type Props = {
  root: CrossCountry.DetailedProduct;
  selectedShade: CrossCountry.DetailedProduct;
};

export default function ProductBeautyServices(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const contentfulLocale = contentfulUtils.mapLocale(router.locale);
  const revieveConfig = useObjectConfigurationValue("App:RevieveConfigs");
  const trisVideosConfig = useObjectConfigurationValue("App:Ecommerce:TrisVideos");
  const videoUrlForProduct = trisVideosConfig?.[props.root.backend_id];
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const videoElement = videoRef.current;

    const handleVideoEnd = () => {
      if (videoElement) {
        videoElement.load();
      }
    };

    videoElement?.addEventListener("ended", handleVideoEnd);

    return () => {
      videoElement?.removeEventListener("ended", handleVideoEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!videoRef.current]);

  const trisApiCall = async (
    backendId: string,
    fallbackTris?: string
  ): Promise<{ tris: Entry<Editorials.Asset>[] }> => {
    if (fallbackTris) {
      return fetch(`/api/next/contentful/tris/?locale=${contentfulLocale}&fallbackTris=${fallbackTris}`)
        .then((res) => res.json())
        .catch((err) => {
          Logger.instance.error(err);
          return { tris: [] };
        });
    } else {
      return fetch(`/api/next/contentful/tris/?locale=${contentfulLocale}&backendId=${backendId}`)
        .then((res) => res.json())
        .catch((err) => {
          Logger.instance.error(err);
          return { tris: [] };
        });
    }
  };

  const { data: trisImages, isLoading } = useQuery({
    queryKey: ["trisImages", props.selectedShade.parent_product_ids ?? props.selectedShade.backend_id],
    staleTime: Infinity,
    queryFn: async () => {
      // get tris images if present
      let images = await trisApiCall(props.selectedShade.backend_id);
      // if selected has no images try with parent
      if (images.tris.length == 0 && props.root.backend_id != props.selectedShade.backend_id) {
        images = await trisApiCall(props.root.backend_id);
      }

      const trisSorted = images.tris
        .filter((image) => !!image.fields && !!image.fields.title && !!image.fields.file)
        .sort((image1, image2) => image1.fields.title.localeCompare(image2.fields.title));

      // else fetch from assigned contentful category
      if (trisSorted.length < 3) {
        const fallbackTrisCategoryBinding = props.selectedShade?.ecomm_online_category_master
          ? await trisApiCall("", props.selectedShade.ecomm_online_category_master)
          : undefined;

        if (fallbackTrisCategoryBinding) {
          const fallbackTris = fallbackTrisCategoryBinding.tris;
          if (fallbackTris?.length) {
            const trisImagesSuffix = trisSorted.map((trisMedia) => Number(trisMedia.fields.title.slice(-1)));
            for (let i = 0; i < 3; ++i) {
              if (fallbackTris[i] && !trisImagesSuffix.includes(i)) {
                trisSorted.splice(i, 0, fallbackTris[i]);
              }
            }
          }
        }
      }

      return trisSorted
        .filter((image) => image.fields.file)
        .map((image) => ({
          url: image.fields.file.url,
          description: image.fields.description ?? "",
        }));
    },
    enabled: !!props.root && !!props.selectedShade,
  });

  useScript(process.env.NEXT_PUBLIC_REVIEVE_API_URL);

  const hasServices =
    props.selectedShade.skin_care_diagnostic ||
    props.selectedShade.try_it_on ||
    props.selectedShade.foundation_finder_enabled;

  if (trisImages?.length === 0) {
    return null;
  }

  if (CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_ENABLE_CLOUDFRONT_ASSETS) && trisImages) {
    trisImages.forEach((trisImage) => {
      trisImage.url = trisImage.url.replace(
        Constants.ASSETS_CDN_IMAGES_SOURCE.CONTENTFUL,
        Constants.ASSETS_CDN_IMAGES_SOURCE.CLOUDFRONT
      );
    });
  }

  return (
    <Grid className={styles.container}>
      {revieveConfig && hasServices && (
        <div className={styles.buttonCol}>
          <div>
            <h3>{t("generic.product_services")}</h3>
            <p>{t("generic.product_services_desc")}</p>
          </div>
          {props.selectedShade.skin_care_diagnostic && (
            <RevieveModalTrigger
              revieveType="skincare"
              variant="serviceMedium"
              className={styles.revieveBtn}
              buttonText={t("generic.product_services_skin")}
            />
          )}
          {props.selectedShade.try_it_on && (
            <RevieveModalTrigger
              revieveType="virtualTryOn"
              variant="serviceMedium"
              className={styles.revieveBtn}
              buttonText={t("generic.product_services_makeup")}
            />
          )}
          {props.selectedShade.foundation_finder_enabled && (
            <RevieveModalTrigger
              revieveType="foundationFinder"
              variant="serviceMedium"
              className={styles.revieveBtn}
              buttonText={t("generic.product_services_foundation")}
            />
          )}
        </div>
      )}
      {isLoading ? (
        <SkeletonTris hasServices={hasServices} />
      ) : (
        <div className={classNames(styles.mosaic, !hasServices && styles.noServices)}>
          {trisImages?.[2] ? (
            <div className={styles.mosaicMedia}>
              {videoUrlForProduct ? (
                <video
                  ref={videoRef}
                  src={videoUrlForProduct}
                  width="100%"
                  height="100%"
                  poster={`https:${trisImages[2].url}`}
                  muted
                  controls
                  controlsList="nodownload"
                  playsInline
                  className={styles.video}
                />
              ) : (
                <NextImage
                  src={`https:${trisImages[2].url}`}
                  alt={trisImages[2].description}
                  fill
                  loader={imageUtils.contentfulImageLoader}
                />
              )}
            </div>
          ) : (
            <div className={styles.mosaicMedia} />
          )}
          {trisImages?.[0] && (
            <div className={styles.mosaicMedia}>
              <NextImage
                src={`https:${trisImages[0].url}`}
                alt={trisImages[0].description}
                fill
                loader={imageUtils.contentfulImageLoader}
              />
            </div>
          )}
          {trisImages?.[1] && (
            <div className={styles.mosaicMedia}>
              <NextImage
                src={`https:${trisImages[1].url}`}
                alt={trisImages[1].description}
                fill
                loader={imageUtils.contentfulImageLoader}
              />
            </div>
          )}
        </div>
      )}
    </Grid>
  );
}
